// List of business entities — entry point for /pricing/be
import { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBusinessEntitiesQuery } from './api';
import type { BusinessEntityPricingListItem } from './api';
import { PricingTypeEnum, SubscriptionPlanEnum, SubscriptionPlanFrequencyEnum } from '@/types/pricing';

const ALL = 'all';

export function BusinessEntityPricingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useBusinessEntitiesQuery();

  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState(ALL);
  const [freqFilter, setFreqFilter] = useState(ALL);
  const [pricingFilter, setPricingFilter] = useState(ALL);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  const rows = useMemo<BusinessEntityPricingListItem[]>(() => {
    // The generic useBusinessEntitiesQuery returns unknown[]; cast here.
    const all = (data ?? []) as BusinessEntityPricingListItem[];
    return all
      .filter((be) => {
        if (search) {
          const term = search.toLowerCase();
          if (!be.businessEntityName?.toLowerCase().includes(term)) return false;
        }
        if (planFilter !== ALL && be.subscriptionPlan !== planFilter) return false;
        if (freqFilter !== ALL && be.subscriptionFrequency !== freqFilter) return false;
        if (pricingFilter !== ALL && be.pricingType?.toLowerCase() !== pricingFilter) return false;
        return true;
      });
  }, [data, search, planFilter, freqFilter, pricingFilter]);

  const paged = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const pricingChipColor = (type: string) =>
    type?.toLowerCase() === PricingTypeEnum.CUSTOM ? 'warning' : 'default';

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {t('pages.pricing.be_pricing.title', { defaultValue: 'Business Entity Pricing' })}
      </Typography>

      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('pages.pricing.be_pricing.subtitle', {
            defaultValue: 'Manage pricing configuration per business entity.',
          })}
        </Typography>

        {/* Filters */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap">
          <TextField
            size="small"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder={t('pages.pricing.be_pricing.table.search_be', {
              defaultValue: 'Search business entity…',
            })}
            sx={{ minWidth: 220 }}
          />

          <Select
            size="small"
            value={pricingFilter}
            onChange={(e) => { setPricingFilter(e.target.value); setPage(0); }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value={ALL}>
              {t('pages.pricing.filter.pricing_filter.all', { defaultValue: 'All pricing' })}
            </MenuItem>
            {Object.values(PricingTypeEnum).map((v) => (
              <MenuItem key={v} value={v}>
                {t(`pages.pricing.filter.pricing_filter.${v}`, { defaultValue: v })}
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            value={planFilter}
            onChange={(e) => { setPlanFilter(e.target.value); setPage(0); }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value={ALL}>
              {t('pages.pricing.filter.subscription_plan_filter.all', { defaultValue: 'All plans' })}
            </MenuItem>
            {Object.values(SubscriptionPlanEnum).map((v) => (
              <MenuItem key={v} value={v}>
                {t(`pages.pricing.subscription_plans.${v}`, { defaultValue: v })}
              </MenuItem>
            ))}
          </Select>

          <Select
            size="small"
            value={freqFilter}
            onChange={(e) => { setFreqFilter(e.target.value); setPage(0); }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value={ALL}>
              {t('pages.pricing.filter.subscription_plan_frequency_filter.all', {
                defaultValue: 'All frequencies',
              })}
            </MenuItem>
            {Object.values(SubscriptionPlanFrequencyEnum)
              .filter((v) => v !== '')
              .map((v) => (
                <MenuItem key={v} value={v}>
                  {t(`pages.pricing.subscription_plan_frequency.${v}`, { defaultValue: v })}
                </MenuItem>
              ))}
          </Select>
        </Stack>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t('pages.pricing.be_pricing.table.businessEntity', {
                        defaultValue: 'Business Entity',
                      })}
                    </TableCell>
                    <TableCell>
                      {t('pages.pricing.be_pricing.table.subscriptionPlan', {
                        defaultValue: 'Subscription Plan',
                      })}
                    </TableCell>
                    <TableCell>
                      {t('pages.pricing.be_pricing.table.subscriptionFrequency', {
                        defaultValue: 'Frequency',
                      })}
                    </TableCell>
                    <TableCell>
                      {t('pages.pricing.be_pricing.table.pricing', { defaultValue: 'Pricing' })}
                    </TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {t('global.no_results', { defaultValue: 'No results found' })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paged.map((be) => (
                      <TableRow key={be.businessEntityId} hover>
                        <TableCell>{be.businessEntityName}</TableCell>
                        <TableCell>
                          {be.subscriptionPlan
                            ? t(`pages.pricing.subscription_plans.${be.subscriptionPlan}`, {
                                defaultValue: be.subscriptionPlan,
                              })
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {be.subscriptionFrequency
                            ? t(
                                `pages.pricing.subscription_plan_frequency.${be.subscriptionFrequency}`,
                                { defaultValue: be.subscriptionFrequency },
                              )
                            : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            color={pricingChipColor(be.pricingType)}
                            label={t(
                              `pages.pricing.be_pricing.table.labels.${be.pricingType?.toUpperCase() ?? 'DEFAULT'}`,
                              { defaultValue: be.pricingType ?? 'DEFAULT' },
                            )}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityIcon />}
                            onClick={() =>
                              navigate(`/pricing/be/details/${be.businessEntityId}`)
                            }
                          >
                            {t('global.view', { defaultValue: 'View' })}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={rows.length}
              page={page}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]}
              onPageChange={(_, p) => setPage(p)}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
